/* @flow */
/* eslint-disable arrow-body-style */
import React, { useCallback, useEffect, useMemo } from 'react';
import Label from 'components/atoms/label/react/Label';
import Input from 'components/atoms/input/react/Input';
import Multiselect from 'components/organisms/multiselect/react/ControlledMultiselect';
import Button from 'components/atoms/button/Button';
import type { Option } from 'components/organisms/multiselect/react/ControlledMultiselect';
import { t, tp } from 'javascripts/utils/withTranslation';
import type {
  Item,
  Status,
  Authority,
  Data,
} from 'components/organisms/authority-search/types';
import ErrorBoundary from 'javascripts/utils/ErrorBoundary';
import useDebounce from 'javascripts/hooks/useDebounce';
import Fuse from 'fuse.js';
import AuthorityCard from './AuthorityCard';
import VibaStatus from './VibaStatus';
import { searchAuthorities } from '../authority-search-util';
import { AUTHORITY_SEARCH_MAX_SCORE } from '../constants';
import AuthorityAutocomplete from './AuthorityAutocomplete';

const AuthoritySearch = ({ items = [], filter }: Data): React$MixedElement => {
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [queryText, setQueryText] = React.useState('');
  const [results, setResults] = React.useState(items || []);

  const handleSearch = (e: any) => {
    e.preventDefault();
    setQueryText(searchValue);
  };

  const handleClear = () => {
    setSearchValue('');
    searchInputRef.current?.focus();
  };

  const fuse = useMemo(
    () =>
      new Fuse(items, {
        includeScore: true,
        keys: ['associatedcommunitiesarray'],
        ignoreLocation: true,
        threshold: 0.07, // This will get things like Altdorf Esslingen without the "(...)"
        fieldNormWeight: 2,
        includeMatches: true,
        minMatchCharLength: 2,
        findAllMatches: true,
        shouldSort: true,
      }),
    [items],
  );

  const maxScore = AUTHORITY_SEARCH_MAX_SCORE; // this will just keep the most relevant results

  useEffect(() => {
    const fetchData = async () => {
      const res = await searchAuthorities(items, queryText, {
        fuse,
        maxScore,
      });
      setResults(res);
    };
    fetchData();
  }, [queryText]);

  return (
    <>
      <ErrorBoundary>
        <form
          className="form list-container__header authority-search__form"
          role="search"
          onSubmit={handleSearch}
        >
          <div className="form__field">
            <AuthorityAutocomplete
              fuse={fuse}
              maxScore={maxScore}
              items={items}
              presetValue=""
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              ref={searchInputRef}
              handleClear={handleClear}
              setItems={setResults}
            />
          </div>
          <div className="form__buttons">
            <Button icon="search" type="submit" className="button--large">
              {t('Baurechtsbehörde finden')}
            </Button>
          </div>
        </form>
      </ErrorBoundary>
      <div className="list-container__content authority-search__result">
        <div
          className="list-container__list-headline"
          aria-live="polite"
          aria-atomic="true"
        >
          <p className="headline headline--6">
            {results.length === 1 ? (
              t('Zuständige Baurechtsbehörde')
            ) : (
              <>
                {results.length}{' '}
                {tp('Baurechtsbehörde', 'Baurechtsbehörden', results.length)}
              </>
            )}
            <span className="u-hidden-visually">{t('gefunden')}</span>
          </p>
        </div>
        <div className="authority-search__legend">
          <span className="f-w-bold">{t('Legende')}:</span>
          {Object.entries(filter || {}).map(([key, value]) => (
            <VibaStatus
              key={key}
              keyNumber={Number(key)}
              text={String(value)}
            />
          ))}
        </div>
        {results?.map((item) => (
          <ErrorBoundary
            key={item.orgid}
            fallbackComponent={() => (
              <div>{t('Fehler beim Anzeigen der Behörde')}</div>
            )}
          >
            <AuthorityCard
              {...item}
              highlighted={undefined} // the Icon inside IconLink component throws an error because of the classnames if this is not set
              vibastate={item.vibastate}
              vibalink={item.vibalink}
              vibalinktext={filter?.[item.vibastate || 0]}
              key={item.orgid}
            />
          </ErrorBoundary>
        ))}
      </div>
    </>
  );
};
export default AuthoritySearch;
